import { Button, Group, Text, Tooltip } from "@mantine/core";
import {
  IconAlertTriangleFilled,
  IconCircleCheckFilled
} from "@tabler/icons-react";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import { useIsOverflow } from "../../../../../hooks/useIsOverflow";
import { getDefaultMRTOptions } from "../../../../../mantine/getDefaultMRTOptions";
import { ImportLogMessage_type } from "../../../../../types/api.types";
import { THEME_VARS } from "../../../../../utils/constants";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { luxonDateTimeToBackendDateOrDateTime } from "../../../../../utils/dates/luxonDateTimeToBackendDateOrDateTime";
import {
  BasicConfirmationModal,
  BasicConfirmationModalIcon
} from "../../../../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";
import type { ImportLog } from "../../../ThirdPartySystems.types";
import "./ImportLogsTable.scss";

interface ErrorPopupData {
  message: string | null;
  originalMessage: string | null;
}

interface ImportLogsTableProps {
  isLoading: boolean;
  logs: Array<ImportLog>;
  meterNames: Record<number, string>;
  numRows: number;
  pageIndex: number;
  pageSize: number;
  onFetchData: (state: { page: number; pageSize: number }) => void;
}

function ImportLogsTable({
  isLoading,
  logs,
  meterNames,
  numRows,
  pageIndex,
  pageSize,
  onFetchData
}: ImportLogsTableProps) {
  const { t } = useTranslation();
  const [shownOriginalMessage, setShownOriginalMessage] =
    useState<ErrorPopupData | null>(null);

  function handleShowOriginalMessage(
    message: string | null,
    originalMessage: string | null
  ) {
    setShownOriginalMessage({ message, originalMessage });
  }

  const cols: Array<MRT_ColumnDef<ImportLog>> = [
    {
      header: "Status",
      accessorKey: "message_type",
      size: 30,
      Cell: ({ row }) => {
        const isSuccess =
          row.original.message_type === ImportLogMessage_type.Erfolg;
        return (
          <Text c={isSuccess ? "success" : "error"}>
            {isSuccess ? "Erfolg" : "Fehler"}
          </Text>
        );
      }
    },
    {
      header: "Zeitpunkt",
      accessorKey: "timestamp",
      size: 120
    },
    {
      header: "Zähler",
      accessorKey: "meter",
      Cell: ({ row }) =>
        typeof row.original.meter === "number"
          ? meterNames[row.original.meter]
          : null
    },
    {
      header: "Meldung",
      accessorKey: "message",
      minSize: 200,
      Cell: ({ row }) => (
        <MessageCell
          message={row.original.message ?? null}
          messageType={row.original.message_type}
          originalMessage={row.original.original_message ?? null}
          onClickShowOriginalMessage={() =>
            handleShowOriginalMessage(
              row.original.message ?? null,
              row.original.original_message ?? null
            )
          }
        />
      )
    },
    {
      header: "Angefragter Zeitraum",
      accessorKey: "start_date",
      size: 170,
      Cell: ({ row }) => (
        <TimespanCell
          endDate={row.original.end_date}
          startDate={row.original.start_date}
        />
      )
    }
  ];

  const table = useMantineReactTable<ImportLog>({
    ...getDefaultMRTOptions({
      emptyRowsFallbackText: "Keine Historie vorhanden",
      hideBorder: true
    }),
    state: { isLoading: isLoading, pagination: { pageIndex, pageSize } },
    enableSorting: false,
    enableTopToolbar: false,
    manualPagination: true,
    rowCount: numRows,
    onPaginationChange: (pagination) => {
      if (typeof pagination === "function") {
        const currentState = { pageIndex, pageSize };
        const newState = pagination(currentState);
        onFetchData({ page: newState.pageIndex, pageSize: newState.pageSize });
      }
    },

    columns: cols,
    data: logs
  });

  return (
    <>
      <MantineReactTable table={table} />
      {shownOriginalMessage && (
        <BasicConfirmationModal
          icon={BasicConfirmationModalIcon.Error}
          opened={!!shownOriginalMessage}
          title={shownOriginalMessage.message ?? undefined}
          toggleModal={() => setShownOriginalMessage(null)}
          onConfirm={() => setShownOriginalMessage(null)}
        >
          <ReactMarkdown>
            {shownOriginalMessage.originalMessage ?? t("errors.UnknownError")}
          </ReactMarkdown>
        </BasicConfirmationModal>
      )}
    </>
  );
}

interface MessageCellProps {
  message: string | null;
  messageType: ImportLogMessage_type;
  originalMessage: string | null;
  onClickShowOriginalMessage: () => void;
}

function MessageCell({
  message,
  messageType,
  originalMessage,
  onClickShowOriginalMessage
}: MessageCellProps) {
  const { overflowRef, isOverflow } = useIsOverflow();

  return (
    <Tooltip
      className="MessageCell"
      disabled={!isOverflow || !message}
      label={message}
    >
      <Group gap="xs" wrap="nowrap">
        {messageType === ImportLogMessage_type.Fehler ? (
          <IconAlertTriangleFilled color={THEME_VARS.error[6]} />
        ) : (
          <IconCircleCheckFilled color="teal" />
        )}
        <div className="message-box" ref={overflowRef}>
          <span>{message}</span>
        </div>
        {originalMessage && (
          <Button
            h="auto"
            variant="transparent"
            onClick={onClickShowOriginalMessage}
          >
            Mehr
          </Button>
        )}
      </Group>
    </Tooltip>
  );
}

interface TimespanCellProps {
  startDate?: string | null;
  endDate?: string | null;
}

function TimespanCell({ startDate, endDate }: TimespanCellProps) {
  const startDateTime = startDate
    ? backendDateOrDateTimeToLuxonDateTime(startDate)
    : null;
  const endDateTime = endDate
    ? backendDateOrDateTimeToLuxonDateTime(endDate)
    : null;

  const startString = startDateTime
    ? luxonDateTimeToBackendDateOrDateTime(startDateTime)
    : "";
  const endString = endDateTime
    ? luxonDateTimeToBackendDateOrDateTime(endDateTime)
    : "";

  const dateDisplay =
    startString && endString
      ? `${startString} - ${endString}`
      : startString || endString || "";

  return <span>{dateDisplay}</span>;
}

export { ImportLogsTable, ImportLogsTableProps };
